/* eslint-disable indent */
import { memo, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Switch from '@mui/material/Switch'
import timezone from 'dayjs/plugin/timezone'
import type { ICourse } from '@store/features/course/course.types'
import {
  deleteCourseThunk,
  updateActivationCourseThunk,
  updateClubToClubCourseThunk
} from '@store/features/course/course.actions'
import { setPopUpData } from '@store/features/general/general.slice'
import { useAppDispatch } from '@store/hooks'
import { Button, LinkTo } from '@components/common'
import { PAGE_ROUTES_BASE_PRIVATE } from '@appTypes/enums/pages'
import { COLORS } from '@collections/colors'
import ClosuresDialog from '@components/ClosuresDialog'
import { IClosure } from '@store/features/closure/closure.types'
import API from '@services'
import {
  Section,
  Title,
  TitleRow,
  StatusChips,
  SubTitles,
  StyledDoubleBox,
  StyledAddressBox,
  ActionsFooter,
  ActionsGrid,
  ClubToClubSetting,
  ClubToClubHeader,
  ClubToClubLabel,
  ClubToClubHelperText,
  StyledNoteBox,
  ValueContainer,
  NoteValueContainer,
  MainWrapper
} from './styled'

dayjs.extend(utc)
dayjs.extend(timezone)

interface ICourseBox extends ICourse {}

const outlinedActionSx = {
  color: COLORS.green,
  borderColor: COLORS.green,
  '&:hover': {
    borderColor: COLORS.green,
    backgroundColor: 'rgba(4, 126, 80, 0.06)'
  }
}

const deleteActionSx = {
  color: COLORS.red,
  borderColor: COLORS.red,
  '&:hover': {
    borderColor: COLORS.red,
    backgroundColor: 'rgba(235, 87, 87, 0.06)'
  }
}

const CourseBox = ({
  id,
  name,
  phone,
  email,
  address,
  guestFee,
  isActive,
  clubToClubEnabled,
  requestEmailCc,
  memberFee,
  notes,
  schedule
}: ICourseBox) => {
  const dispatch = useAppDispatch()
  const [loader, setLoader] = useState<string | null>(null)
  const [isAct, setIsAct] = useState<boolean>(isActive)
  const [isClubToClub, setIsClubToClub] = useState<boolean>(clubToClubEnabled)
  const [isClosuseDialogOpen, setIsClosureDialogOpen] = useState(false)
  const [closures, setClosures] = useState<IClosure[]>([])

  const fetchClosures = async () => {
    try {
      const result = await API.closure.getClosures(id)

      setClosures(result.data)
    } catch (err) {
      console.log('Error fetching Closures -> ', err)
    }
  }

  useEffect(() => {
    fetchClosures()
  }, [id])

  const getClosureEndDate = () => {
    const now = dayjs.tz(new Date(), 'America/New_York')

    const match = closures.find(({ startDate, endDate }) => {
      const start = dayjs.tz(startDate, 'America/New_York')
      const end = dayjs.tz(endDate, 'America/New_York')
      return ((now.isAfter(start) && now.isBefore(end)) || now.isSame(start) || now.isSame(end))
    })

    return match ? dayjs.tz(match.endDate, 'America/New_York').toDate() : null
  }

  const closureEndDate = getClosureEndDate()

  const handleClubToClubToggle = () => {
    setLoader('clubToClub')
    dispatch(
      updateClubToClubCourseThunk({
        id,
        name,
        phone,
        email,
        address,
        guestFee,
        memberFee,
        notes,
        schedule,
        isActive,
        clubToClubEnabled: !isClubToClub,
        requestEmailCc
      })
    )
      .then(() => {
        setLoader(null)
        setIsClubToClub((prev) => !prev)
      })
      .catch(() => {
        setLoader(null)
      })
  }

  return (
    <Section>
      <TitleRow>
        <Title isBlocked={!!closureEndDate}>
          {name}
          {closureEndDate ? (
            <span>
              Blocked until:{' '}
              {closureEndDate.toLocaleDateString('en-US', {
                timeZone: 'America/New_York'
              })}
            </span>
          ) : null}
        </Title>
        <StatusChips>
          {!isAct ? (
            <Chip label='Hidden from members' size='small' variant='outlined' />
          ) : null}
          {isClubToClub ? (
            <Chip
              label='Club-to-club on'
              size='small'
              sx={{
                backgroundColor: 'rgba(4, 126, 80, 0.12)',
                color: COLORS.green,
                fontWeight: 600
              }}
            />
          ) : null}
        </StatusChips>
      </TitleRow>
      <MainWrapper>
        <StyledDoubleBox>
          <Box>
            <SubTitles>Phone Number</SubTitles>
            <ValueContainer>{phone}</ValueContainer>
          </Box>
          <Box>
            <SubTitles>Email</SubTitles>
            <ValueContainer>{email}</ValueContainer>
          </Box>
        </StyledDoubleBox>
        {requestEmailCc?.length ? (
          <StyledAddressBox>
            <SubTitles>CC Emails (club-to-club requests)</SubTitles>
            <ValueContainer>{requestEmailCc.join(', ')}</ValueContainer>
          </StyledAddressBox>
        ) : null}
        <StyledAddressBox>
          <SubTitles>Course Address</SubTitles>
          <ValueContainer>{address}</ValueContainer>
        </StyledAddressBox>
        <StyledDoubleBox>
          <Box>
            <SubTitles>Member Fee</SubTitles>
            <ValueContainer>${memberFee}</ValueContainer>
          </Box>
          <Box>
            <SubTitles>Guest Fee</SubTitles>
            <ValueContainer>${guestFee}</ValueContainer>
          </Box>
        </StyledDoubleBox>
        <StyledNoteBox>
          <SubTitles>Course Note</SubTitles>
          <NoteValueContainer>{notes}</NoteValueContainer>
        </StyledNoteBox>
      </MainWrapper>

      <ActionsFooter>
        <ClubToClubSetting>
          <ClubToClubHeader>
            <ClubToClubLabel>Club-to-club requests</ClubToClubLabel>
            <Switch
              checked={isClubToClub}
              disabled={loader === 'clubToClub'}
              onChange={handleClubToClubToggle}
              color='success'
            />
          </ClubToClubHeader>
          <ClubToClubHelperText>
            {isClubToClub
              ? 'Other clubs can receive email requests for this course.'
              : 'Turn on to allow the club to receive email requests for this course.'}
          </ClubToClubHelperText>
        </ClubToClubSetting>

        <ActionsGrid>
          <LinkTo href={`${PAGE_ROUTES_BASE_PRIVATE.UPDATE_COURSE_BASE}/${id}`}>
            <Button variant='contained' color='primary' type='button' fullWidth>
              Edit course
            </Button>
          </LinkTo>

          <Button
            variant='outlined'
            type='button'
            fullWidth
            sx={outlinedActionSx}
            onClick={() => setIsClosureDialogOpen(true)}
          >
            Closures
          </Button>

          <Button
            variant='outlined'
            type='button'
            fullWidth
            isLoading={loader === 'hide'}
            sx={outlinedActionSx}
            onClick={() => {
              setLoader('hide')
              dispatch(
                updateActivationCourseThunk({
                  id,
                  name,
                  phone,
                  email,
                  address,
                  guestFee,
                  memberFee,
                  notes,
                  schedule,
                  isActive: !isActive,
                  clubToClubEnabled,
                  requestEmailCc
                })
              )
                .then(() => {
                  setLoader(null)
                  setIsAct((prev) => !prev)
                })
                .catch(() => {
                  setLoader(null)
                })
            }}
          >
            {isAct ? 'Hide from members' : 'Show to members'}
          </Button>

          <Button
            variant='outlined'
            type='button'
            fullWidth
            isLoading={loader === 'delete'}
            sx={deleteActionSx}
            onClick={() => {
              setLoader('delete')
              dispatch(deleteCourseThunk({ id }))
                .then(() => {
                  dispatch(setPopUpData({ status: true, popUpId: id }))
                  setLoader(null)
                })
                .catch(() => {
                  setLoader(null)
                })
            }}
          >
            Delete
          </Button>
        </ActionsGrid>
      </ActionsFooter>

      {isClosuseDialogOpen ? (
        <ClosuresDialog
          courseName={name}
          isOpen={isClosuseDialogOpen}
          close={() => setIsClosureDialogOpen(false)}
          courseId={id}
          closures={closures}
          refetch={fetchClosures}
        />
      ) : null}
    </Section>
  )
}

export default memo(CourseBox)
